// This file is auto-generated by @hey-api/openapi-ts

import { type DefaultError, queryOptions, type UseMutationOptions } from '@tanstack/react-query';

import { client } from '../client.gen';
import { agentRunsGet, agentRunsList, agentsActivate, agentsCreateVersion, agentsGet, agentsList, agentsVersions, calendarEventsList, googleCalendarWebhookNotify, googleOAuthCallback, googleOAuthStart, healthHealth, jobsDescribe, jobsRun, jobsSync, meMe, type Options, personaConnectionsList, personaConnectionsUnlink, personasCreate, personasGet, personasList, personasRemove, personasUpdate, planningList, planningPlan, realtimeToken, taskCaptureCreate, tasksCreate, tasksList, tasksRemove, tasksUpdate, usersList, usersSetPersonas } from '../sdk.gen';
import type { AgentRunsGetData, AgentRunsGetError, AgentRunsGetResponse, AgentRunsListData, AgentRunsListError, AgentRunsListResponse, AgentsActivateData, AgentsActivateError, AgentsActivateResponse, AgentsCreateVersionData, AgentsCreateVersionError, AgentsCreateVersionResponse, AgentsGetData, AgentsGetError, AgentsGetResponse, AgentsListData, AgentsListResponse, AgentsVersionsData, AgentsVersionsError, AgentsVersionsResponse, CalendarEventsListData, CalendarEventsListError, CalendarEventsListResponse, GoogleCalendarWebhookNotifyData, GoogleCalendarWebhookNotifyError, GoogleCalendarWebhookNotifyResponse, GoogleOAuthCallbackData, GoogleOAuthCallbackError, GoogleOAuthCallbackResponse, GoogleOAuthStartData, GoogleOAuthStartError, GoogleOAuthStartResponse, HealthHealthData, HealthHealthResponse, JobsDescribeData, JobsDescribeResponse, JobsRunData, JobsSyncData, JobsSyncResponse, MeMeData, MeMeError, MeMeResponse, PersonaConnectionsListData, PersonaConnectionsListError, PersonaConnectionsListResponse, PersonaConnectionsUnlinkData, PersonaConnectionsUnlinkError, PersonaConnectionsUnlinkResponse, PersonasCreateData, PersonasCreateError, PersonasCreateResponse, PersonasGetData, PersonasGetError, PersonasGetResponse, PersonasListData, PersonasListResponse, PersonasRemoveData, PersonasRemoveError, PersonasRemoveResponse, PersonasUpdateData, PersonasUpdateError, PersonasUpdateResponse, PlanningListData, PlanningListError, PlanningListResponse, PlanningPlanData, PlanningPlanError, PlanningPlanResponse, RealtimeTokenData, RealtimeTokenResponse, TaskCaptureCreateData, TaskCaptureCreateError, TaskCaptureCreateResponse, TasksCreateData, TasksCreateError, TasksCreateResponse, TasksListData, TasksListError, TasksListResponse, TasksRemoveData, TasksRemoveError, TasksRemoveResponse, TasksUpdateData, TasksUpdateError, TasksUpdateResponse, UsersListData, UsersListResponse, UsersSetPersonasData, UsersSetPersonasError, UsersSetPersonasResponse } from '../types.gen';

export type QueryKey<TOptions extends Options> = [
    Pick<TOptions, 'baseUrl' | 'body' | 'headers' | 'path' | 'query'> & {
        _id: string;
        _infinite?: boolean;
        tags?: ReadonlyArray<string>;
    }
];

const createQueryKey = <TOptions extends Options>(id: string, options?: TOptions, infinite?: boolean, tags?: ReadonlyArray<string>): [
    QueryKey<TOptions>[0]
] => {
    const params: QueryKey<TOptions>[0] = { _id: id, baseUrl: options?.baseUrl || (options?.client ?? client).getConfig().baseUrl } as QueryKey<TOptions>[0];
    if (infinite) {
        params._infinite = infinite;
    }
    if (tags) {
        params.tags = tags;
    }
    if (options?.body) {
        params.body = options.body;
    }
    if (options?.headers) {
        params.headers = options.headers;
    }
    if (options?.path) {
        params.path = options.path;
    }
    if (options?.query) {
        params.query = options.query;
    }
    return [params];
};

export const realtimeTokenQueryKey = (options?: Options<RealtimeTokenData>) => createQueryKey('realtimeToken', options);

/**
 * Get an Ably token request
 *
 * Signed by the API. Lets the browser subscribe to Sage's `sage:*` channels for an hour. It can't publish.
 */
export const realtimeTokenOptions = (options?: Options<RealtimeTokenData>) => queryOptions<RealtimeTokenResponse, DefaultError, RealtimeTokenResponse, ReturnType<typeof realtimeTokenQueryKey>>({
    queryFn: async ({ queryKey, signal }) => {
        const { data } = await realtimeToken({
            ...options,
            ...queryKey[0],
            signal,
            throwOnError: true
        });
        return data;
    },
    queryKey: realtimeTokenQueryKey(options)
});

export const agentsListQueryKey = (options?: Options<AgentsListData>) => createQueryKey('agentsList', options);

/**
 * List agents
 */
export const agentsListOptions = (options?: Options<AgentsListData>) => queryOptions<AgentsListResponse, DefaultError, AgentsListResponse, ReturnType<typeof agentsListQueryKey>>({
    queryFn: async ({ queryKey, signal }) => {
        const { data } = await agentsList({
            ...options,
            ...queryKey[0],
            signal,
            throwOnError: true
        });
        return data;
    },
    queryKey: agentsListQueryKey(options)
});

export const agentsGetQueryKey = (options: Options<AgentsGetData>) => createQueryKey('agentsGet', options);

/**
 * Get an agent
 */
export const agentsGetOptions = (options: Options<AgentsGetData>) => queryOptions<AgentsGetResponse, AgentsGetError, AgentsGetResponse, ReturnType<typeof agentsGetQueryKey>>({
    queryFn: async ({ queryKey, signal }) => {
        const { data } = await agentsGet({
            ...options,
            ...queryKey[0],
            signal,
            throwOnError: true
        });
        return data;
    },
    queryKey: agentsGetQueryKey(options)
});

export const agentsVersionsQueryKey = (options: Options<AgentsVersionsData>) => createQueryKey('agentsVersions', options);

/**
 * List an agent's prompt versions
 *
 * Newest first.
 */
export const agentsVersionsOptions = (options: Options<AgentsVersionsData>) => queryOptions<AgentsVersionsResponse, AgentsVersionsError, AgentsVersionsResponse, ReturnType<typeof agentsVersionsQueryKey>>({
    queryFn: async ({ queryKey, signal }) => {
        const { data } = await agentsVersions({
            ...options,
            ...queryKey[0],
            signal,
            throwOnError: true
        });
        return data;
    },
    queryKey: agentsVersionsQueryKey(options)
});

/**
 * Save a new prompt version
 *
 * Versions are never changed. Saving adds the next one and, unless `activate` is false, makes runs use it.
 */
export const agentsCreateVersionMutation = (options?: Partial<Options<AgentsCreateVersionData>>): UseMutationOptions<AgentsCreateVersionResponse, AgentsCreateVersionError, Options<AgentsCreateVersionData>> => {
    const mutationOptions: UseMutationOptions<AgentsCreateVersionResponse, AgentsCreateVersionError, Options<AgentsCreateVersionData>> = {
        mutationFn: async (fnOptions) => {
            const { data } = await agentsCreateVersion({
                ...options,
                ...fnOptions,
                throwOnError: true
            });
            return data;
        }
    };
    return mutationOptions;
};

/**
 * Use a prompt version
 *
 * Makes runs use this version, e.g. to roll back to an earlier one.
 */
export const agentsActivateMutation = (options?: Partial<Options<AgentsActivateData>>): UseMutationOptions<AgentsActivateResponse, AgentsActivateError, Options<AgentsActivateData>> => {
    const mutationOptions: UseMutationOptions<AgentsActivateResponse, AgentsActivateError, Options<AgentsActivateData>> = {
        mutationFn: async (fnOptions) => {
            const { data } = await agentsActivate({
                ...options,
                ...fnOptions,
                throwOnError: true
            });
            return data;
        }
    };
    return mutationOptions;
};

export const agentRunsListQueryKey = (options?: Options<AgentRunsListData>) => createQueryKey('agentRunsList', options);

/**
 * List agent runs
 *
 * Newest first. Follow a run live on the `sage:agent-runs` Ably channel (`run.updated`).
 */
export const agentRunsListOptions = (options?: Options<AgentRunsListData>) => queryOptions<AgentRunsListResponse, AgentRunsListError, AgentRunsListResponse, ReturnType<typeof agentRunsListQueryKey>>({
    queryFn: async ({ queryKey, signal }) => {
        const { data } = await agentRunsList({
            ...options,
            ...queryKey[0],
            signal,
            throwOnError: true
        });
        return data;
    },
    queryKey: agentRunsListQueryKey(options)
});

export const agentRunsGetQueryKey = (options: Options<AgentRunsGetData>) => createQueryKey('agentRunsGet', options);

/**
 * Get an agent run
 */
export const agentRunsGetOptions = (options: Options<AgentRunsGetData>) => queryOptions<AgentRunsGetResponse, AgentRunsGetError, AgentRunsGetResponse, ReturnType<typeof agentRunsGetQueryKey>>({
    queryFn: async ({ queryKey, signal }) => {
        const { data } = await agentRunsGet({
            ...options,
            ...queryKey[0],
            signal,
            throwOnError: true
        });
        return data;
    },
    queryKey: agentRunsGetQueryKey(options)
});

export const personasListQueryKey = (options?: Options<PersonasListData>) => createQueryKey('personasList', options);

/**
 * List personas
 */
export const personasListOptions = (options?: Options<PersonasListData>) => queryOptions<PersonasListResponse, DefaultError, PersonasListResponse, ReturnType<typeof personasListQueryKey>>({
    queryFn: async ({ queryKey, signal }) => {
        const { data } = await personasList({
            ...options,
            ...queryKey[0],
            signal,
            throwOnError: true
        });
        return data;
    },
    queryKey: personasListQueryKey(options)
});

/**
 * Create a persona
 */
export const personasCreateMutation = (options?: Partial<Options<PersonasCreateData>>): UseMutationOptions<PersonasCreateResponse, PersonasCreateError, Options<PersonasCreateData>> => {
    const mutationOptions: UseMutationOptions<PersonasCreateResponse, PersonasCreateError, Options<PersonasCreateData>> = {
        mutationFn: async (fnOptions) => {
            const { data } = await personasCreate({
                ...options,
                ...fnOptions,
                throwOnError: true
            });
            return data;
        }
    };
    return mutationOptions;
};

/**
 * Delete a persona
 *
 * Soft delete: the persona is hidden but kept in the database.
 */
export const personasRemoveMutation = (options?: Partial<Options<PersonasRemoveData>>): UseMutationOptions<PersonasRemoveResponse, PersonasRemoveError, Options<PersonasRemoveData>> => {
    const mutationOptions: UseMutationOptions<PersonasRemoveResponse, PersonasRemoveError, Options<PersonasRemoveData>> = {
        mutationFn: async (fnOptions) => {
            const { data } = await personasRemove({
                ...options,
                ...fnOptions,
                throwOnError: true
            });
            return data;
        }
    };
    return mutationOptions;
};

export const personasGetQueryKey = (options: Options<PersonasGetData>) => createQueryKey('personasGet', options);

/**
 * Get a persona
 */
export const personasGetOptions = (options: Options<PersonasGetData>) => queryOptions<PersonasGetResponse, PersonasGetError, PersonasGetResponse, ReturnType<typeof personasGetQueryKey>>({
    queryFn: async ({ queryKey, signal }) => {
        const { data } = await personasGet({
            ...options,
            ...queryKey[0],
            signal,
            throwOnError: true
        });
        return data;
    },
    queryKey: personasGetQueryKey(options)
});

/**
 * Update a persona
 *
 * Changes only the fields sent.
 */
export const personasUpdateMutation = (options?: Partial<Options<PersonasUpdateData>>): UseMutationOptions<PersonasUpdateResponse, PersonasUpdateError, Options<PersonasUpdateData>> => {
    const mutationOptions: UseMutationOptions<PersonasUpdateResponse, PersonasUpdateError, Options<PersonasUpdateData>> = {
        mutationFn: async (fnOptions) => {
            const { data } = await personasUpdate({
                ...options,
                ...fnOptions,
                throwOnError: true
            });
            return data;
        }
    };
    return mutationOptions;
};

export const usersListQueryKey = (options?: Options<UsersListData>) => createQueryKey('usersList', options);

/**
 * List users
 *
 * Everyone who has signed in, with the personas that are theirs.
 */
export const usersListOptions = (options?: Options<UsersListData>) => queryOptions<UsersListResponse, DefaultError, UsersListResponse, ReturnType<typeof usersListQueryKey>>({
    queryFn: async ({ queryKey, signal }) => {
        const { data } = await usersList({
            ...options,
            ...queryKey[0],
            signal,
            throwOnError: true
        });
        return data;
    },
    queryKey: usersListQueryKey(options)
});

/**
 * Set a user's personas
 *
 * Replaces the user's personas with exactly the ones sent. Anyone can set anyone's.
 */
export const usersSetPersonasMutation = (options?: Partial<Options<UsersSetPersonasData>>): UseMutationOptions<UsersSetPersonasResponse, UsersSetPersonasError, Options<UsersSetPersonasData>> => {
    const mutationOptions: UseMutationOptions<UsersSetPersonasResponse, UsersSetPersonasError, Options<UsersSetPersonasData>> = {
        mutationFn: async (fnOptions) => {
            const { data } = await usersSetPersonas({
                ...options,
                ...fnOptions,
                throwOnError: true
            });
            return data;
        }
    };
    return mutationOptions;
};

export const meMeQueryKey = (options?: Options<MeMeData>) => createQueryKey('meMe', options);

/**
 * The signed-in user
 */
export const meMeOptions = (options?: Options<MeMeData>) => queryOptions<MeMeResponse, MeMeError, MeMeResponse, ReturnType<typeof meMeQueryKey>>({
    queryFn: async ({ queryKey, signal }) => {
        const { data } = await meMe({
            ...options,
            ...queryKey[0],
            signal,
            throwOnError: true
        });
        return data;
    },
    queryKey: meMeQueryKey(options)
});

export const jobsDescribeQueryKey = (options?: Options<JobsDescribeData>) => createQueryKey('jobsDescribe', options);

/**
 * Inngest endpoint
 *
 * Inngest calls this to list and run Sage's background jobs. It's public, but Inngest signs every request and the SDK rejects unsigned ones (outside local dev).
 */
export const jobsDescribeOptions = (options?: Options<JobsDescribeData>) => queryOptions<JobsDescribeResponse, DefaultError, JobsDescribeResponse, ReturnType<typeof jobsDescribeQueryKey>>({
    queryFn: async ({ queryKey, signal }) => {
        const { data } = await jobsDescribe({
            ...options,
            ...queryKey[0],
            signal,
            throwOnError: true
        });
        return data;
    },
    queryKey: jobsDescribeQueryKey(options)
});

/**
 * Inngest endpoint
 *
 * Inngest calls this to list and run Sage's background jobs. It's public, but Inngest signs every request and the SDK rejects unsigned ones (outside local dev).
 */
export const jobsRunMutation = (options?: Partial<Options<JobsRunData>>): UseMutationOptions<unknown, DefaultError, Options<JobsRunData>> => {
    const mutationOptions: UseMutationOptions<unknown, DefaultError, Options<JobsRunData>> = {
        mutationFn: async (fnOptions) => {
            const { data } = await jobsRun({
                ...options,
                ...fnOptions,
                throwOnError: true
            });
            return data;
        }
    };
    return mutationOptions;
};

/**
 * Inngest endpoint
 *
 * Inngest calls this to list and run Sage's background jobs. It's public, but Inngest signs every request and the SDK rejects unsigned ones (outside local dev).
 */
export const jobsSyncMutation = (options?: Partial<Options<JobsSyncData>>): UseMutationOptions<JobsSyncResponse, DefaultError, Options<JobsSyncData>> => {
    const mutationOptions: UseMutationOptions<JobsSyncResponse, DefaultError, Options<JobsSyncData>> = {
        mutationFn: async (fnOptions) => {
            const { data } = await jobsSync({
                ...options,
                ...fnOptions,
                throwOnError: true
            });
            return data;
        }
    };
    return mutationOptions;
};

/**
 * Start connecting a Google account
 *
 * Returns Google's consent page for the browser to go to, and sets a 10 minute cookie the callback checks.
 */
export const googleOAuthStartMutation = (options?: Partial<Options<GoogleOAuthStartData>>): UseMutationOptions<GoogleOAuthStartResponse, GoogleOAuthStartError, Options<GoogleOAuthStartData>> => {
    const mutationOptions: UseMutationOptions<GoogleOAuthStartResponse, GoogleOAuthStartError, Options<GoogleOAuthStartData>> = {
        mutationFn: async (fnOptions) => {
            const { data } = await googleOAuthStart({
                ...options,
                ...fnOptions,
                throwOnError: true
            });
            return data;
        }
    };
    return mutationOptions;
};

export const googleOAuthCallbackQueryKey = (options?: Options<GoogleOAuthCallbackData>) => createQueryKey('googleOAuthCallback', options);

/**
 * Google OAuth callback
 *
 * Google sends the browser here. Saves the account, links it to the persona, then redirects (303) to the persona's page with `?connected=<id>`, or to `/personas?connectionError=<message>` if it failed.
 */
export const googleOAuthCallbackOptions = (options?: Options<GoogleOAuthCallbackData>) => queryOptions<GoogleOAuthCallbackResponse, GoogleOAuthCallbackError, GoogleOAuthCallbackResponse, ReturnType<typeof googleOAuthCallbackQueryKey>>({
    queryFn: async ({ queryKey, signal }) => {
        const { data } = await googleOAuthCallback({
            ...options,
            ...queryKey[0],
            signal,
            throwOnError: true
        });
        return data;
    },
    queryKey: googleOAuthCallbackQueryKey(options)
});

export const personaConnectionsListQueryKey = (options: Options<PersonaConnectionsListData>) => createQueryKey('personaConnectionsList', options);

/**
 * List a persona's connections
 */
export const personaConnectionsListOptions = (options: Options<PersonaConnectionsListData>) => queryOptions<PersonaConnectionsListResponse, PersonaConnectionsListError, PersonaConnectionsListResponse, ReturnType<typeof personaConnectionsListQueryKey>>({
    queryFn: async ({ queryKey, signal }) => {
        const { data } = await personaConnectionsList({
            ...options,
            ...queryKey[0],
            signal,
            throwOnError: true
        });
        return data;
    },
    queryKey: personaConnectionsListQueryKey(options)
});

/**
 * Unlink a connection from a persona
 *
 * Other personas keep it. Once no persona uses it, the connection is deleted.
 */
export const personaConnectionsUnlinkMutation = (options?: Partial<Options<PersonaConnectionsUnlinkData>>): UseMutationOptions<PersonaConnectionsUnlinkResponse, PersonaConnectionsUnlinkError, Options<PersonaConnectionsUnlinkData>> => {
    const mutationOptions: UseMutationOptions<PersonaConnectionsUnlinkResponse, PersonaConnectionsUnlinkError, Options<PersonaConnectionsUnlinkData>> = {
        mutationFn: async (fnOptions) => {
            const { data } = await personaConnectionsUnlink({
                ...options,
                ...fnOptions,
                throwOnError: true
            });
            return data;
        }
    };
    return mutationOptions;
};

export const calendarEventsListQueryKey = (options: Options<CalendarEventsListData>) => createQueryKey('calendarEventsList', options);

/**
 * List calendar events
 *
 * Events from every enabled calendar of the personas asked for (all personas if none), that overlap `from` to `to`. For one day in the user's time zone, send its local midnights.
 */
export const calendarEventsListOptions = (options: Options<CalendarEventsListData>) => queryOptions<CalendarEventsListResponse, CalendarEventsListError, CalendarEventsListResponse, ReturnType<typeof calendarEventsListQueryKey>>({
    queryFn: async ({ queryKey, signal }) => {
        const { data } = await calendarEventsList({
            ...options,
            ...queryKey[0],
            signal,
            throwOnError: true
        });
        return data;
    },
    queryKey: calendarEventsListQueryKey(options)
});

/**
 * Google Calendar change notification
 *
 * Google calls this when a watched calendar changes. It's public, so the channel token Google echoes back (an HMAC of the channel id) is checked. A known channel queues a sync of its connection.
 */
export const googleCalendarWebhookNotifyMutation = (options?: Partial<Options<GoogleCalendarWebhookNotifyData>>): UseMutationOptions<GoogleCalendarWebhookNotifyResponse, GoogleCalendarWebhookNotifyError, Options<GoogleCalendarWebhookNotifyData>> => {
    const mutationOptions: UseMutationOptions<GoogleCalendarWebhookNotifyResponse, GoogleCalendarWebhookNotifyError, Options<GoogleCalendarWebhookNotifyData>> = {
        mutationFn: async (fnOptions) => {
            const { data } = await googleCalendarWebhookNotify({
                ...options,
                ...fnOptions,
                throwOnError: true
            });
            return data;
        }
    };
    return mutationOptions;
};

export const healthHealthQueryKey = (options?: Options<HealthHealthData>) => createQueryKey('healthHealth', options);

/**
 * Liveness check
 */
export const healthHealthOptions = (options?: Options<HealthHealthData>) => queryOptions<HealthHealthResponse, DefaultError, HealthHealthResponse, ReturnType<typeof healthHealthQueryKey>>({
    queryFn: async ({ queryKey, signal }) => {
        const { data } = await healthHealth({
            ...options,
            ...queryKey[0],
            signal,
            throwOnError: true
        });
        return data;
    },
    queryKey: healthHealthQueryKey(options)
});

export const tasksListQueryKey = (options?: Options<TasksListData>) => createQueryKey('tasksList', options);

/**
 * List tasks
 *
 * Open tasks first (soonest due, then most important), then done ones (latest first). Changes arrive live on the `sage:tasks` Ably channel.
 */
export const tasksListOptions = (options?: Options<TasksListData>) => queryOptions<TasksListResponse, TasksListError, TasksListResponse, ReturnType<typeof tasksListQueryKey>>({
    queryFn: async ({ queryKey, signal }) => {
        const { data } = await tasksList({
            ...options,
            ...queryKey[0],
            signal,
            throwOnError: true
        });
        return data;
    },
    queryKey: tasksListQueryKey(options)
});

/**
 * Add a task
 */
export const tasksCreateMutation = (options?: Partial<Options<TasksCreateData>>): UseMutationOptions<TasksCreateResponse, TasksCreateError, Options<TasksCreateData>> => {
    const mutationOptions: UseMutationOptions<TasksCreateResponse, TasksCreateError, Options<TasksCreateData>> = {
        mutationFn: async (fnOptions) => {
            const { data } = await tasksCreate({
                ...options,
                ...fnOptions,
                throwOnError: true
            });
            return data;
        }
    };
    return mutationOptions;
};

/**
 * Delete a task
 *
 * Soft delete: the task is hidden but kept in the database.
 */
export const tasksRemoveMutation = (options?: Partial<Options<TasksRemoveData>>): UseMutationOptions<TasksRemoveResponse, TasksRemoveError, Options<TasksRemoveData>> => {
    const mutationOptions: UseMutationOptions<TasksRemoveResponse, TasksRemoveError, Options<TasksRemoveData>> = {
        mutationFn: async (fnOptions) => {
            const { data } = await tasksRemove({
                ...options,
                ...fnOptions,
                throwOnError: true
            });
            return data;
        }
    };
    return mutationOptions;
};

/**
 * Update a task
 *
 * Changes only the fields sent. Send `done: true` to tick it off, or `false` to reopen it.
 */
export const tasksUpdateMutation = (options?: Partial<Options<TasksUpdateData>>): UseMutationOptions<TasksUpdateResponse, TasksUpdateError, Options<TasksUpdateData>> => {
    const mutationOptions: UseMutationOptions<TasksUpdateResponse, TasksUpdateError, Options<TasksUpdateData>> = {
        mutationFn: async (fnOptions) => {
            const { data } = await tasksUpdate({
                ...options,
                ...fnOptions,
                throwOnError: true
            });
            return data;
        }
    };
    return mutationOptions;
};

/**
 * Turn text into tasks
 *
 * Queues a run of the task capture agent, which adds, changes or ticks off tasks from the text, and returns its id straight away. Follow the run on the `sage:agent-runs` Ably channel or at `GET /agent-runs/{id}`; tasks it changes arrive on `sage:tasks`.
 */
export const taskCaptureCreateMutation = (options?: Partial<Options<TaskCaptureCreateData>>): UseMutationOptions<TaskCaptureCreateResponse, TaskCaptureCreateError, Options<TaskCaptureCreateData>> => {
    const mutationOptions: UseMutationOptions<TaskCaptureCreateResponse, TaskCaptureCreateError, Options<TaskCaptureCreateData>> = {
        mutationFn: async (fnOptions) => {
            const { data } = await taskCaptureCreate({
                ...options,
                ...fnOptions,
                throwOnError: true
            });
            return data;
        }
    };
    return mutationOptions;
};

export const planningListQueryKey = (options: Options<PlanningListData>) => createQueryKey('planningList', options);

/**
 * List planned task blocks
 *
 * Time the day planner set aside for tasks, on the days from `from` to `to` (inclusive), in start order. Changes arrive live on the `sage:planning` Ably channel.
 */
export const planningListOptions = (options: Options<PlanningListData>) => queryOptions<PlanningListResponse, PlanningListError, PlanningListResponse, ReturnType<typeof planningListQueryKey>>({
    queryFn: async ({ queryKey, signal }) => {
        const { data } = await planningList({
            ...options,
            ...queryKey[0],
            signal,
            throwOnError: true
        });
        return data;
    },
    queryKey: planningListQueryKey(options)
});

/**
 * Plan a day
 *
 * Queues a run of the day planner agent, which fits the day's tasks into its free time, and returns its id straight away. Follow the run on the `sage:agent-runs` Ably channel or at `GET /agent-runs/{id}`; the new plan arrives on `sage:planning`.
 */
export const planningPlanMutation = (options?: Partial<Options<PlanningPlanData>>): UseMutationOptions<PlanningPlanResponse, PlanningPlanError, Options<PlanningPlanData>> => {
    const mutationOptions: UseMutationOptions<PlanningPlanResponse, PlanningPlanError, Options<PlanningPlanData>> = {
        mutationFn: async (fnOptions) => {
            const { data } = await planningPlan({
                ...options,
                ...fnOptions,
                throwOnError: true
            });
            return data;
        }
    };
    return mutationOptions;
};
